import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"

export type QuotaOrLimit = z.infer<typeof QuotaOrLimit>
const QuotaOrLimit = z.union([z.number(), z.literal("unlimited")])

export type Quotas = z.infer<typeof Quotas>
const Quotas = z.object({
  apiCalls: QuotaOrLimit,
  bandwidth: QuotaOrLimit,
})

export type Limits = z.infer<typeof Limits>
const Limits = z.object({
  apiCalls: QuotaOrLimit,
  bandwidth: QuotaOrLimit,
  users: QuotaOrLimit,
  locales: QuotaOrLimit,
})

export type Subscription = z.infer<typeof Subscription>
const Subscription = z.object({
  id: z.string(),
  nickname: z.string(),
  lookupKey: z.string().nullable(),
  quotas: Quotas,
  limits: Limits,
  product: z.string(),
  paymentFailed: z.boolean().optional(),
  interval: z.enum(["month", "year"]),
  wasAutoUpgradedRecently: z.boolean().optional(),
})

export function getSubscription(): Promise<Subscription> {
  const url = new URL(
    "billing/subscription",
    env.PRISMIC_BILLING_SERVICE_BASE_URL,
  )
  url.searchParams.set("repository", getRepositoryName())

  return request(url, { ...requestOptions, okType: Subscription })
}

const requestOptions = {
  credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
}

/**
 * Converts a `string` in the `YYYY-MM-DD` format to a `Date`.
 */
export const stringToDate = z.string().date().pipe(z.coerce.date())

export type BillingPeriod = z.infer<typeof BillingPeriod>
const BillingPeriod = z.object({
  startDate: stringToDate,
  endDate: stringToDate,
  plan: Subscription,
})

export async function getBillingPeriods(): Promise<BillingPeriod[]> {
  const url = new URL(
    "billing/subscription/periods",
    env.PRISMIC_BILLING_SERVICE_BASE_URL,
  )
  url.searchParams.set("repository", getRepositoryName())

  return (
    await request(url, {
      ...requestOptions,
      okType: GetBillingPeriodsResponse,
    })
  ).results
}

const GetBillingPeriodsResponse = z.object({
  results: z.array(BillingPeriod),
})
